import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/axios';
import { User, AuthTokens } from '../types';

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  _refreshPromise: Promise<void> | null;
  setAuth: (user: User, tokens: AuthTokens) => void;
  fetchUser: () => Promise<void>;
  updateUser: (user: User) => void;
  logout: () => void;
  refreshToken: () => Promise<void>;
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>()(persist(
  (set, get) => ({
    _refreshPromise: null,
    user: null,
    tokens: null,
    isAuthenticated: false,

    setAuth: (user: User, tokens: AuthTokens) => {
      set({ user, tokens, isAuthenticated: true });
    },

    fetchUser: async () => {
      const { tokens } = get();
      if (!tokens?.access_token) return;

      const response = await api.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${tokens.access_token}` },
      });
      set({ user: response.data });
    },

    updateUser: (user: User) => {
      set({ user });
    },

    logout: () => {
      set({
        user: null,
        tokens: null,
        isAuthenticated: false,
      });
    },

    refreshToken: async () => {
      // Prevent multiple simultaneous refresh calls
      const existing = get()._refreshPromise;
      if (existing) return existing;

      const { tokens } = get();
      if (!tokens?.refresh_token) {
        get().logout();
        return;
      }

      const promise = (async () => {
        try {
          const response = await api.post('/api/auth/refresh', {
            refresh_token: tokens.refresh_token,
          });
          const { access_token, refresh_token } = response.data;
          set({ tokens: { access_token, refresh_token }, _refreshPromise: null });
        } catch {
          set({ _refreshPromise: null });
          get().logout();
        }
      })();

      set({ _refreshPromise: promise });
      return promise;
    },

    hydrate: () => {
      const { tokens, user } = get();
      if (tokens && user) {
        set({ isAuthenticated: true });
      }
    },
  }),
  {
    name: 'auth-storage',
    partialize: (state) => ({
      tokens: state.tokens,
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      // _refreshPromise excluded from persistence
    }),
  }
));
